import { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';

const usePermissions = () => {
  const { user } = useContext(AuthContext);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      // Important: keep loading=true during permission refresh to avoid
      // transient "access denied" flicker before backend response arrives.
      setLoading(true);
      fetchPermissions();
    } else {
      setPermissions([]);
      setLoading(false);
    }
  }, [user]);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/auth/me/permissions');
      setPermissions(response.data.permissions || []);
    } catch (error) {
      console.error('Failed to fetch permissions', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = useCallback((permission) => {
    // Permissions are already properly loaded for all users including super admins
    return permissions.includes(permission);
  }, [permissions]);

  const hasAnyPermission = useCallback((permissionList) => {
    return permissionList.some(p => hasPermission(p));
  }, [hasPermission]);

  const hasAllPermissions = useCallback((permissionList) => {
    return permissionList.every(p => hasPermission(p));
  }, [hasPermission]);

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  };
};

export default usePermissions;
